export const useApps = defineStore('apps', () => {
  const publicRuntimeConfig = useRuntimeConfig().public;
  const { currentUserHasSomeGroups } = useCurrentUserStore();
  const { staticApps, dynamicApps } = useAppConfig();
  const all: globalThis.Ref<AppConfig[]> = ref([]);
  const currentSlug = ref('');

  const bySlug = computed(() => {
    return all.value.reduce<{ [appName: string]: AppConfig }>((acc, app) => {
      return { ...acc, [app.slug]: app };
    }, {});
  });
  const grouped = computed(() => {
    const groupMap = all.value.reduce((groupMap, app) => {
      if (!app.group) {
        groupMap._noGroup.push(app);
      }
      else {
        groupMap[app.group] = groupMap[app.group] || [];
        groupMap[app.group].push(app);
      }

      return groupMap;
    }, { _noGroup: [] } as { [key: string]: AppConfig[] });
    return Object
      .entries(groupMap)
      .map(([groupName, apps]) => ({ name: groupName !== '_noGroup' ? groupName : '', apps }));
  });
  const current = computed(() => bySlug.value[currentSlug.value]);

  /**
   * If an app config has the `external_link_config` property, checks the
   * public runtime config for a property matching that value.
   * If there is one, gets the value of that config property and assigns
   * it to a copy of the app config, then returns it.
   *
   * Otherwise, the original app config is returned.
   *
   * @param app
   */
  const assignExternalLinkFromConfig = <T extends { external_link?: string; external_link_config?: string }>(app: T): T => {
    if (!app.external_link_config) {
      return app;
    }
    const externalLink = publicRuntimeConfig[app.external_link_config] as string | undefined;
    if (externalLink) {
      const newApp = {
        ...app,
        external_link: externalLink,
      };
      delete newApp.external_link_config;
      return newApp;
    }
    return app;
  };

  const fetch = async () => {
    if (all.value.length > 0) {
      return;
    }

    const api = useApi();
    try {
      const serverAppSlugs = (await api.getAllAppsForUser()).results.map(app => app.slug);
      const filteredDynamicApps = dynamicApps.filter(app => serverAppSlugs.includes(app.slug));
      const allApps = await asyncMap([...filteredDynamicApps, ...staticApps], async app => {
        if (app.append_link) {
          app.append_link = assignExternalLinkFromConfig(app.append_link);

          if (
            app.append_link.show_if &&
            !(await currentUserHasSomeGroups(ensureArray(app.append_link.show_if.user_has_group)))
          ) {
            delete app.append_link;
          }
        }

        return assignExternalLinkFromConfig(app);
      });
      // Sorts the apps by their order numbers, highest to lowest.
      // Apps with no explicit order are assigned 0.
      allApps.sort((a, b) => {
        const order = (app: AppConfig) => app.order || 0;
        return order(b) - order(a);
      });
      all.value = allApps;
    }
    catch (err) {
      console.error('Error fetching apps for store:', (err as any).message);
    }
  };

  const setCurrent = (appSlug: string) => {
    if (appSlug in bySlug.value) {
      currentSlug.value = appSlug;
    }
  };

  return { all, bySlug, current, grouped, fetch, setCurrent };
});
